import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, Loader2, ChevronLeft, ChevronRight, Trash2, X } from 'lucide-react';
import api from '@/api';

const PERIOD_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'custom', label: 'Custom' },
];

export function NewsFeedTab({ onDeleteTopic }) {
  const [topics, setTopics] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ categories: [], regions: [] });

  // Filter state
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [region, setRegion] = useState('');
  const [period, setPeriod] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const loadTopics = useCallback(async (resetPage = false) => {
    setLoading(true);
    const p = resetPage ? 0 : page;
    if (resetPage) setPage(0);
    try {
      const params = {
        q: search,
        category,
        region,
        period: period === 'custom' ? '' : period,
        date_from: period === 'custom' ? dateFrom : '',
        date_to: period === 'custom' ? dateTo : '',
        skip: p * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      const data = await api.adminGetTopics(params);
      setTopics(data.topics || []);
      setTotal(data.total || 0);
      if (data.filters) setFilters(data.filters);
    } catch (e) {
      console.error('Failed to load topics:', e);
    } finally {
      setLoading(false);
    }
  }, [search, category, region, period, dateFrom, dateTo, page]);

  useEffect(() => { loadTopics(); }, [page]);

  const handleSearch = () => loadTopics(true);
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(); };
  const clearFilters = () => {
    setSearch(''); setCategory(''); setRegion(''); setPeriod(''); setDateFrom(''); setDateTo('');
    setPage(0);
    // Trigger reload after state clears
    setTimeout(() => loadTopics(true), 0);
  };

  const handleDelete = async (id) => {
    if (onDeleteTopic) await onDeleteTopic(id);
    loadTopics();
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasActiveFilters = search || category || region || period;

  return (
    <div data-testid="news-feed-tab" className="mt-2 space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          data-testid="news-feed-search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search articles..."
          className="w-full pl-10 pr-20 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-sm outline-none focus:border-blue-300 transition-colors"
        />
        <button data-testid="news-feed-search-btn" onClick={handleSearch}
          className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 transition-colors">
          Search
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-2 items-center">
        <Filter size={14} className="text-gray-400 shrink-0" />
        <select data-testid="news-feed-category" value={category} onChange={e => { setCategory(e.target.value); }}
          className="px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs outline-none">
          <option value="">All Categories</option>
          {filters.categories.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
        <select data-testid="news-feed-region" value={region} onChange={e => { setRegion(e.target.value); }}
          className="px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs outline-none">
          <option value="">All Countries</option>
          {filters.regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <select data-testid="news-feed-period" value={period} onChange={e => { setPeriod(e.target.value); }}
          className="px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs outline-none">
          {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <button data-testid="news-feed-apply" onClick={handleSearch}
          className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors">
          Apply
        </button>
        {hasActiveFilters && (
          <button data-testid="news-feed-clear" onClick={clearFilters}
            className="px-2 py-1.5 rounded-lg bg-gray-100 text-gray-500 text-xs font-semibold hover:bg-gray-200 flex items-center gap-1 transition-colors">
            <X size={12} /> Clear
          </button>
        )}
      </div>

      {/* Custom date range */}
      {period === 'custom' && (
        <div className="flex gap-2 items-center">
          <input data-testid="news-feed-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs outline-none" />
          <span className="text-xs text-gray-400">to</span>
          <input data-testid="news-feed-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs outline-none" />
        </div>
      )}

      {/* Total count */}
      <div className="flex items-center justify-between px-1">
        <p data-testid="news-feed-total" className="text-xs font-bold text-gray-500">
          {total.toLocaleString()} article{total !== 1 ? 's' : ''} found
        </p>
        {totalPages > 1 && (
          <p className="text-[10px] text-gray-400">Page {page + 1} of {totalPages}</p>
        )}
      </div>

      {/* Results list */}
      {loading ? (
        <div className="text-center py-10"><Loader2 size={24} className="animate-spin mx-auto text-gray-400" /></div>
      ) : topics.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-gray-400">No articles match your filters</p>
        </div>
      ) : (
        <div className="space-y-2">
          {topics.map(t => (
            <div key={t.id} data-testid={`topic-row-${t.id}`} className="bg-white rounded-xl border border-gray-100 p-3 flex items-center justify-between">
              <div className="min-w-0 flex-1 mr-2">
                <p className="text-sm font-medium text-gray-900 truncate">{t.title}</p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">{t.category?.replace(/_/g, ' ')}</span>
                  {t.region && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-500">{t.region}</span>}
                  <span className="text-[9px] text-gray-400">Score: {t.trend_score}</span>
                  <span className="text-[9px] text-gray-400">{t.source}</span>
                  {t.created_at && <span className="text-[9px] text-gray-300">{new Date(t.created_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <button data-testid={`delete-topic-${t.id}`} onClick={() => handleDelete(t.id)}
                className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors shrink-0">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-2">
          <button data-testid="news-feed-prev" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 disabled:opacity-40 transition-colors">
            <ChevronLeft size={14} /> Prev
          </button>
          <span className="text-xs font-bold text-gray-500">{page + 1} / {totalPages}</span>
          <button data-testid="news-feed-next" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-gray-200 disabled:opacity-40 transition-colors">
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
